'use client'

import { motion } from 'framer-motion'
import Link from 'next/link'
import { ChevronRight, Home, Phone, Shield, Zap } from 'lucide-react'
import { WhatsAppIcon } from './WhatsAppIcon'
import { getWhatsAppUrl, PHONE_CALL_URL } from '@/lib/whatsapp'

interface BreadcrumbItem {
  label: string
  href: string
}

interface PageHeaderProps {
  title: string
  subtitle?: string
  breadcrumb?: BreadcrumbItem[]
  showActions?: boolean
  serviceName?: string
}

export function PageHeader({
  title,
  subtitle,
  breadcrumb,
  showActions = true,
  serviceName,
}: PageHeaderProps) {
  const whatsappUrl = getWhatsAppUrl({
    type: 'service',
    service: serviceName || title,
  })

  return (
    <div
      className="relative overflow-hidden py-12 sm:py-16 md:py-20"
      style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 60%, #2563EB 100%)' }}
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-16 -right-16 w-64 h-64 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(217,119,6,0.2), transparent)' }}
        />
        <div
          className="absolute bottom-0 left-1/4 w-48 h-48 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.06), transparent)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.05) 1.5px, transparent 1.5px)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="container-narrow relative z-10">
        {/* Breadcrumb */}
        {breadcrumb && breadcrumb.length > 0 && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-blue-200 text-xs sm:text-sm mb-4"
          >
            <Link href="/" className="flex items-center gap-1 hover:text-white transition-colors duration-200">
              <Home className="w-3.5 h-3.5" />
              Home
            </Link>
            {breadcrumb.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1.5">
                <ChevronRight className="w-3.5 h-3.5 text-blue-300" />
                {idx === breadcrumb.length - 1 ? (
                  <span className="text-white font-medium">{item.label}</span>
                ) : (
                  <Link href={item.href} className="hover:text-white transition-colors duration-200">
                    {item.label}
                  </Link>
                )}
              </span>
            ))}
          </motion.nav>
        )}

        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-3 text-balance text-white leading-tight"
        >
          {title}
        </motion.h1>

        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-base sm:text-lg text-blue-100 max-w-2xl text-balance leading-relaxed mb-6"
          >
            {subtitle}
          </motion.p>
        )}

        {/* Hero Conversion Actions & Reassurance Strip */}
        {showActions && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="pt-2"
          >
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <a
                id="pageheader-whatsapp-btn"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm shadow-lg transition-all duration-200 hover:brightness-105 active:scale-98"
                style={{
                  backgroundColor: '#25D366',
                  boxShadow: '0 6px 20px rgba(37, 211, 102, 0.4)',
                }}
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                </span>
                <WhatsAppIcon className="w-4 h-4 fill-white" />
                <span>Chat on WhatsApp</span>
              </a>

              <a
                id="pageheader-call-btn"
                href={PHONE_CALL_URL}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-[#1D4ED8] font-bold text-sm hover:bg-[#EFF6FF] transition-all duration-200 shadow-md active:scale-98"
              >
                <Phone className="w-4 h-4 text-[#1D4ED8]" />
                <span>Call Directly</span>
              </a>
            </div>

            {/* Reassurance Strip */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs font-medium text-blue-100">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                Replies in ~5 Mins
              </span>
              <span className="text-white/40 hidden sm:inline">•</span>
              <span className="inline-flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-[#FCD34D]" />
                100% Confidential & Secure
              </span>
              <span className="text-white/40 hidden sm:inline">•</span>
              <span className="inline-flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-[#FCD34D]" />
                Fast Turnaround
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}
